import React, { useState } from 'react';
import { TierGate, UpgradePrompt } from '../pages/ProtectedRoute';
import { useAuth } from './AuthContext';
import SEO from '../components/SEO';
import { UpworkProposalGenerator, RecruiterReplyGenerator, JobDescriptionMatcher, CronGenerator, APITester } from './NewAITools';

/* ─── TOKENS — exact match to DevTools.jsx ───────────────────────────── */
const T = {
  bg:      '#0a0c10',
  bg2:     '#0e1117',
  bg3:     '#13171f',
  surface: '#1a1f2b',
  border:  'rgba(255,255,255,.07)',
  borderHi:'rgba(255,255,255,.12)',
  white:   '#e8edf5',
  muted:   'rgba(255,255,255,0.50)',
  dim:     'rgba(232,237,245,.22)',
  green:   '#3dd68c',
  red:     '#ff5f57',
  yellow:  '#fbbf24',
  blue:    '#4d9fff',
  purple:  '#b78cff',
  orange:  '#ff9f43',
  cyan:    '#00d4ff',
  mono:    '"JetBrains Mono","Fira Code","Cascadia Code",monospace',
  sans:    '"DM Sans","Outfit","system-ui",sans-serif',
};

/* ─── SHARED ATOMS ───────────────────────────────────────────────────── */
const inputStyle = {
  width:'100%', padding:'8px 12px',
  background:T.bg, border:`1px solid ${T.border}`,
  borderRadius:7, color:T.white,
  fontSize:13, fontFamily:T.sans,
  outline:'none', transition:'border-color .15s',
  boxSizing:'border-box',
};

const taStyle = { ...inputStyle, resize:'vertical', lineHeight:1.65, fontFamily:T.sans };

function Label({ children }) {
  return (
    <div style={{
      fontSize:10.5, fontWeight:700, letterSpacing:'.1em',
      textTransform:'uppercase', color:T.dim, fontFamily:T.sans, marginBottom:5,
    }}>{children}</div>
  );
}

function Field({ label, children }) {
  return <div style={{ marginBottom:12 }}><Label>{label}</Label>{children}</div>;
}

function SelectField({ label, value, onChange, options }) {
  return (
    <Field label={label}>
      <select value={value} onChange={e => onChange(e.target.value)} style={{
        ...inputStyle, cursor:'pointer',
      }}>
        {options.map(o => <option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
      </select>
    </Field>
  );
}

function TextField({ label, value, onChange, placeholder, rows = 2 }) {
  return (
    <Field label={label}>
      <textarea value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ ...taStyle, minHeight: rows * 38 }}/>
    </Field>
  );
}

function RunBtn({ onClick, loading, disabled, label }) {
  return (
    <button onClick={onClick} disabled={disabled || loading} style={{
      padding:'9px 20px', borderRadius:8,
      border:`1px solid ${T.cyan}40`,
      background:T.cyan+'14', color:T.cyan,
      fontSize:13, fontWeight:700, fontFamily:T.sans,
      cursor: disabled||loading ? 'default' : 'pointer',
      opacity: disabled||loading ? .5 : 1,
      display:'inline-flex', alignItems:'center', gap:7,
      transition:'all .15s',
    }}>
      {loading
        ? <><span style={{ width:13, height:13, borderRadius:'50%', border:`2px solid ${T.cyan}30`, borderTopColor:T.cyan, animation:'spin .65s linear infinite', display:'inline-block' }}/> Generating…</>
        : label}
    </button>
  );
}

function ResultBox({ text, loading }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div style={{ marginTop:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
        <Label>Result</Label>
        {text && (
          <button onClick={copy} style={{
            padding:'4px 10px', borderRadius:5, border:`1px solid ${T.border}`,
            background:'transparent', color:copied ? T.green : T.muted,
            fontSize:11.5, fontFamily:T.sans, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:4,
          }}>
            {copied ? '✓ Copied' : '⧉ Copy'}
          </button>
        )}
      </div>
      <div style={{
        padding:'14px 16px', minHeight:120,
        background:T.bg, border:`1px solid ${T.border}`,
        borderRadius:8, fontSize:13.5, color:T.white,
        lineHeight:1.75, whiteSpace:'pre-wrap', fontFamily:T.sans,
      }}>
        {loading
          ? <span style={{ color:T.muted, display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ width:13, height:13, borderRadius:'50%', border:`2px solid ${T.muted}30`, borderTopColor:T.muted, animation:'spin .65s linear infinite', display:'inline-block' }}/>
              Generating…
            </span>
          : text || <span style={{ color:T.dim }}>Output will appear here…</span>}
      </div>
    </div>
  );
}

/* ─── API CALL ───────────────────────────────────────────────────────── */
async function callClaude(prompt) {
  const res = await fetch('https://api.anthropic.com/v1/messages', {
    method:'POST',
    headers:{ 'Content-Type':'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-6',
      max_tokens: 1000,
      messages:[{ role:'user', content:prompt }]
    })
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message);
  return data.content?.[0]?.text || '';
}

/* ─── TOOL COMPONENTS ────────────────────────────────────────────────── */
export function PromptGenerator() {
  const [goal, setGoal] = useState('');
  const [context, setContext] = useState('');
  const [format, setFormat] = useState('detailed');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const { apiFetch } = useAuth();

  const run = async () => {
    if (!goal) return;
    setLoading(true);
    try { 
      const text = await callTool(apiFetch,'prompt', { goal, context, format });
      setResult(text);
      // setResult(await callClaude(`Create a highly effective AI prompt for: "${goal}"\nContext: ${context||'none'}\nFormat: ${format}\n\nWrite only the prompt itself, nothing else. Make it clear, specific, and well-structured.`));
       }
    catch(e) { setResult('Error: '+e.message); }
    setLoading(false);
  };
  return (
    <div>
      <TextField label="What should the AI do?" value={goal} onChange={setGoal} placeholder="e.g. Analyze customer feedback and extract themes" rows={2}/>
      <TextField label="Context / Background" value={context} onChange={setContext} placeholder="e.g. B2B SaaS, 500+ reviews" rows={2}/>
      <SelectField label="Format" value={format} onChange={setFormat} options={['detailed','concise','step-by-step','creative','technical']}/>
      <RunBtn onClick={run} loading={loading} disabled={!goal} label="✦ Generate Prompt"/>
      <ResultBox text={result} loading={loading}/>
    </div>
  );
}

export function EmailGenerator({ resume }) {
  const [purpose, setPurpose]  = useState('follow-up');
  const [recipient, setRecipient] = useState('');
  const [context, setContext]  = useState('');
  const [tone, setTone]        = useState('professional');
  const [result, setResult]    = useState('');
  const [loading, setLoading]  = useState(false);
  const { apiFetch } = useAuth();

  const run = async () => {
    setLoading(true);
    try { 
    const text = await callTool(apiFetch,'email', { purpose, recipient, context, tone });
      setResult(text);
      // setResult(await callClaude(`Write a ${tone} ${purpose} email.\nTo: ${recipient||'client'}\nContext: ${context}\nInclude a subject line. Be concise and effective.`)); 
    }
    catch(e) { setResult('Error: '+e.message); }
    setLoading(false);
  };
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }} className="ai-grid-2">
        <SelectField label="Email Type" value={purpose} onChange={setPurpose} options={[{value:'follow-up',label:'Follow-up'},{value:'introduction',label:'Introduction'},{value:'proposal',label:'Proposal'},{value:'thank-you',label:'Thank You'},{value:'cold outreach',label:'Cold Outreach'},{value:'apology',label:'Apology'}]}/>
        <SelectField label="Tone" value={tone} onChange={setTone} options={['professional','friendly','formal','casual','urgent','empathetic']}/>
      </div>
      <TextField label="Recipient / Role" value={recipient} onChange={setRecipient} placeholder="e.g. Project manager at a startup" rows={1}/>
      <TextField label="Key Points / Context" value={context} onChange={setContext} placeholder="What's the email about?" rows={3}/>
      <RunBtn onClick={run} loading={loading} label="✉ Generate Email"/>
      <ResultBox text={result} loading={loading}/>
    </div>
  );
}

export function LinkedInGenerator({ resume }) {
  const [topic, setTopic]    = useState('');
  const [hook, setHook]      = useState('');
  const [style, setStyle]    = useState('thought-leadership');
  const [result, setResult]  = useState('');
  const [loading, setLoading]= useState(false);
    const { apiFetch } = useAuth();

  const run = async () => {
    if (!topic) return;
    setLoading(true);
    try { 
      const text = await callTool(apiFetch,'linkedin', { topic, hook, style });
      setResult(text);
    }
    catch(e) { setResult('Error: '+e.message); }
    setLoading(false);
  };
  return (
    <div>
      <TextField label="Topic / Achievement" value={topic} onChange={setTopic} placeholder="e.g. Lessons from launching my first SaaS" rows={2}/>
      <TextField label="Specific Angle (optional)" value={hook} onChange={setHook} placeholder="e.g. Start with a surprising stat" rows={1}/>
      <SelectField label="Style" value={style} onChange={setStyle} options={['thought-leadership','personal story','tips and advice','industry insight','achievement','controversial take']}/>
      <RunBtn onClick={run} loading={loading} disabled={!topic} label="💼 Generate Post"/>
      <ResultBox text={result} loading={loading}/>
    </div>
  );
}

export function CoverLetterGenerator({ resume }) {
  const [role, setRole]           = useState('');
  const [company, setCompany]     = useState('');
  const [skills, setSkills]       = useState('');
  const [highlight, setHighlight] = useState('');
  const [result, setResult]       = useState('');
  const [loading, setLoading]     = useState(false);
      const { apiFetch } = useAuth();

  const run = async () => {
    if (!role) return;
    setLoading(true);
    try { 
      const text = await callTool(apiFetch,'cover-letter', { role, company, skills, highlight });
      setResult(text);
    }
    catch(e) { setResult('Error: '+e.message); }
    setLoading(false);
  };
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }} className="ai-grid-2">
        <TextField label="Job Role" value={role} onChange={setRole} placeholder="Senior Backend Engineer" rows={1}/>
        <TextField label="Company" value={company} onChange={setCompany} placeholder="Stripe, Shopify…" rows={1}/>
      </div>
      <TextField label="Key Skills" value={skills?skills:resume?.skills || ''} onChange={setSkills} placeholder="Python, Django, AWS, 5 years…" rows={2}/>
      <TextField label="Top Achievement" value={highlight?highlight:resume?.achievements?.map(a => a).join(', \n') || ''} onChange={setHighlight} placeholder="e.g. Reduced API latency by 60% at previous role" rows={2}/>
      <RunBtn onClick={run} loading={loading} disabled={!role} label="📄 Generate Cover Letter"/>
      <ResultBox text={result} loading={loading}/>
    </div>
  );
}

export function ResumeSummaryGenerator({ resume }) {
  const [role, setRole]    = useState('');
  const [years, setYears]  = useState('');
  const [skills, setSkills]= useState('');
  const [goal, setGoal]    = useState('');
  const [result, setResult]= useState('');
  const [loading, setLoading]= useState(false);
  const { apiFetch } = useAuth();

  const run = async () => {
    if (!role) return;
    setLoading(true);
    try { 
      const text = await callTool(apiFetch,'resume-summary', { role, years, skills, goal });
      setResult(text);
    }
    catch(e) { setResult('Error: '+e.message); }
    setLoading(false);
  };
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }} className="ai-grid-2">
        <TextField label="Target Role" value={role} onChange={setRole} placeholder="Full Stack Developer" rows={1}/>
        <TextField label="Years of Experience" value={years?years:resume?.years_experience || ''} onChange={setYears} placeholder="7" rows={1}/>
      </div>
      <TextField label="Top Skills" value={skills?skills:resume?.skills || ''} onChange={setSkills} placeholder="React, Node.js, PostgreSQL, Docker" rows={1}/>
      <TextField label="Career Goal" value={goal?goal:resume?.career_goal || ''} onChange={setGoal} placeholder="e.g. Transition into engineering management" rows={2}/>
      <RunBtn onClick={run} loading={loading} disabled={!role} label="👤 Generate Summary"/>
      <ResultBox text={result} loading={loading}/>
    </div>
  );
}

export function SQLGenerator() {
  const [schema, setSchema]     = useState('');
  const [question, setQuestion] = useState('');
  const [dialect, setDialect]   = useState('PostgreSQL');
  const [result, setResult]     = useState('');
  const [loading, setLoading]   = useState(false);
  const { apiFetch } = useAuth();

  const run = async () => {
    if (!question) return;
    setLoading(true);
    try { 
      const text = await callTool(apiFetch,'sql', { dialect, question, schema });
      setResult(text);
    }
    catch(e) { setResult('Error: '+e.message); }
    setLoading(false);
  };
  return (
    <div>
      <SelectField label="Dialect" value={dialect} onChange={setDialect} options={['PostgreSQL','MySQL','SQLite','SQL Server','BigQuery']}/>
      <TextField label="Schema (optional)" value={schema} onChange={setSchema} placeholder={"users(id, name, email)\norders(id, user_id, total, status)"} rows={3}/>
      <TextField label="What query do you need?" value={question} onChange={setQuestion} placeholder="e.g. Top 10 users by order value in the last 30 days with email and order count" rows={3}/>
      <RunBtn onClick={run} loading={loading} disabled={!question} label="🗄 Generate SQL"/>
      <ResultBox text={result} loading={loading}/>
    </div>
  );
}

export function RegexGenerator() {
  const [description, setDesc]     = useState('');
  const [example, setExample]= useState('');
  const [lang, setLang]     = useState('JavaScript');
  const [result, setResult] = useState('');
  const [loading, setLoading]= useState(false);
  const { apiFetch } = useAuth();

  const run = async () => {
    if (!description) return;
    setLoading(true);
    try { 
      const text = await callTool(apiFetch,'regex', { description, example, lang });
      setResult(text);
    }
    catch(e) { setResult('Error: '+e.message); }
    setLoading(false);
  };
  return (
    <div>
      <SelectField label="Language" value={lang} onChange={setLang} options={['JavaScript','Python','PHP','Java','Go','Rust','Ruby']}/>
      <TextField label="Describe what to match" value={description} onChange={setDesc} placeholder="e.g. Email addresses, US phone numbers, IPv4 addresses" rows={2}/>
      <TextField label="Example Input (optional)" value={example} onChange={setExample} placeholder="e.g. user@example.com" rows={1}/>
      <RunBtn onClick={run} loading={loading} disabled={!description} label="✦ Generate Regex"/>
      <ResultBox text={result} loading={loading}/>
    </div>
  );
}

export function APIDocsGenerator() {
  const [endpoint, setEndpoint] = useState('');
  const [format, setFormat]     = useState('Markdown');
  const [result, setResult]     = useState('');
  const [loading, setLoading]   = useState(false);
  const { apiFetch } = useAuth();

  const run = async () => {
    if (!endpoint) return;
    setLoading(true);
    try { 
      const text = await callTool(apiFetch,'api-docs', { endpoint, format });
      setResult(text);
    }
    catch(e) { setResult('Error: '+e.message); }
    setLoading(false);
  };
  return (
    <div>
      <SelectField label="Format" value={format} onChange={setFormat} options={['Markdown','OpenAPI YAML','JSDoc','Postman JSON']}/>
      <TextField label="Endpoint / Code" value={endpoint} onChange={setEndpoint} placeholder={"POST /api/users/\nCreates a new user.\nBody: { email, password, name }"} rows={5}/>
      <RunBtn onClick={run} loading={loading} disabled={!endpoint} label="📚 Generate Docs"/>
      <ResultBox text={result} loading={loading}/>
    </div>
  );
}

export function MeetingNotes() {
  const [notes, setNotes]    = useState('');
  const [style, setStyle]    = useState('action-items');
  const [result, setResult]  = useState('');
  const [loading, setLoading]= useState(false);
  const { apiFetch } = useAuth();

  const run = async () => {
    if (!notes) return;
    setLoading(true);
    try { 
      const text = await callTool(apiFetch,'meeting-notes', { notes, style });
      setResult(text);
    }
    catch(e) { setResult('Error: '+e.message); }
    setLoading(false);
  };
  return (
    <div>
      <SelectField label="Summary Style" value={style} onChange={setStyle} options={[{value:'action-items',label:'Action Items & Decisions'},{value:'executive-summary',label:'Executive Summary'},{value:'meeting-minutes',label:'Full Meeting Minutes'}]}/>
      <TextField label="Raw Meeting Notes" value={notes} onChange={setNotes} placeholder="Paste your raw notes or transcript here…" rows={7}/>
      <RunBtn onClick={run} loading={loading} disabled={!notes} label="📋 Summarize"/>
      <ResultBox text={result} loading={loading}/>
    </div>
  );
}

/* ─── TOOL REGISTRY ─────────────────────────────────────────────────── */
const TOOLS = [
  { id:'prompt',   label:'Prompt Generator',    icon:'✦', component:PromptGenerator    },
  { id:'email',    label:'Email Generator',      icon:'✉', component:EmailGenerator    },
  { id:'linkedin', label:'LinkedIn Post',        icon:'💼',component:LinkedInGenerator },
  { id:'cover',    label:'Cover Letter',         icon:'📄',component:CoverLetterGenerator},
  { id:'resume',   label:'Resume Summary',       icon:'👤',component:ResumeSummaryGenerator},
  { id:'sql',      label:'SQL Generator',        icon:'🗄',component:SQLGenerator      },
  { id:'regex',    label:'Regex Generator',      icon:'.*',component:RegexGenerator    },
  { id:'apidocs',  label:'API Docs',             icon:'📚',component:APIDocsGenerator  },
  { id:'meeting',  label:'Meeting Notes',        icon:'📋',component:MeetingNotes      },
  { id:'upwork',    label:'Upwork Proposal',  icon:'📝' ,component:UpworkProposalGenerator},
  { id:'recruiter', label:'Recruiter Reply',  icon:'💼', component:RecruiterReplyGenerator},
  { id:'job',       label:'Job Matcher',      icon:'🎯',component:JobDescriptionMatcher },
  { id:'cron',      label:'Cron Generator',  icon:'⏰', component: CronGenerator },
  { id:'apitest',   label:'API Tester',       icon:'🔌', component: APITester },
];

// async function callTool(endpoint, body) {
//   const { apiFetch } = useAuth();

//   const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
//     const res = await apiFetch(`${API}/api/tools/ai/${endpoint}/`,{
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify(body),
//   });
//   //   if (res?.ok) setData(await res.text);
//   //   setLoading(false);
//   const res = await fetch(`${API}/api/tools/ai/${endpoint}/`, {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify(body),
//   });
//   const data = await res.json();
//   if (data.error) throw new Error(data.error);
//   return data.text;
// }
  

async function callTool(apiFetch,endpoint, body) {

    const res = await apiFetch(`/api/tools/ai/${endpoint}/`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data.text;
}
/* ─── MAIN — matches FinanceTools sidebar layout ─────────────────────── */
export default function AITools() {
  const [active, setActive] = useState('prompt');
  const Tool = TOOLS.find(t => t.id === active)?.component;
  const meta = TOOLS.find(t => t.id === active);


  return (
    <>
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        * { box-sizing:border-box; margin:0; padding:0; }
        ::placeholder { color:${T.dim} !important; }
        textarea:focus, input:focus, select:focus { border-color:rgba(77,159,255,.4)!important; outline:none; }
        select option { background:${T.bg2}; color:${T.white}; }
        @media (max-width:640px) {
          .ai-layout  { grid-template-columns:1fr !important; }
          .ai-sidebar { display:none !important; }
          .ai-mobile-select { display:block !important; }
          .ai-grid-2  { grid-template-columns:1fr !important; }
        }
        @media (min-width:641px) {
          .ai-mobile-select { display:none !important; }
        }
      `}</style>
 <div className="page">
  <TierGate requires="ai_tools" fallback={<UpgradePrompt feature="AI Tools" requiredTier="ai_tools"/>}>

    <section className="section" style={{ paddingTop: 0 }}>
      <div style={{ padding:'28px 0 48px', fontFamily:T.sans }}>
        <div style={{ marginBottom:20 }}>
          <h1 style={{ fontSize:'clamp(1.5rem,3vw,2rem)', fontWeight:900, color:T.white, letterSpacing:'-.02em', marginBottom:5 }}>
            🤖 AI Tools
          </h1>
          <p style={{ fontSize:13, color:T.muted }}>
            Powered by Claude — emails, posts, code, docs in seconds
          </p>
        </div>

        {/* Mobile tool select */}
        <div className="ai-mobile-select" style={{ marginBottom:12 }}>
          <select value={active} onChange={e => setActive(e.target.value)} style={{
            width:'100%', padding:'10px 12px', borderRadius:8,
            background:T.surface, border:`1px solid ${T.borderHi}`,
            color:T.white, fontSize:13, fontFamily:T.sans,
          }}>
            {TOOLS.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
          </select>
        </div>

        {/* Sidebar + panel layout — matches FinanceTools exactly */}
        <div className="ai-layout" style={{
          display:'grid', gridTemplateColumns:'200px 1fr', gap:0,
          border:`1px solid ${T.border}`, borderRadius:12, overflow:'hidden', minHeight:500,
        }}>

          {/* Sidebar */}
          <div className="ai-sidebar" style={{
            borderRight:`1px solid ${T.border}`,
            background:T.bg2, padding:'12px 0',
          }}>
            {TOOLS.map(t => (
              <button key={t.id} onClick={() => setActive(t.id)} style={{
                width:'100%', padding:'9px 16px', border:'none',
                borderLeft:`2px solid ${active===t.id ? T.cyan : 'transparent'}`,
                background: active===t.id ? T.cyan+'10' : 'transparent',
                color: active===t.id ? T.white : T.muted,
                fontSize:12.5, fontFamily:T.sans,
                cursor:'pointer', textAlign:'left',
                display:'flex', alignItems:'center', gap:8,
                transition:'all .13s',
              }}>
                <span style={{ fontSize:14, width:18, textAlign:'center' }}>{t.icon}</span>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tool panel */}
          <div style={{ padding:'22px 24px', background:T.bg3 }}>
            <h2 style={{ fontSize:16, fontWeight:700, color:T.white, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
              <span>{meta?.icon}</span> {meta?.label}
            </h2>
            {Tool && <Tool/>}
          </div>
        </div>

        <div style={{ marginTop:10, textAlign:'center', fontSize:11.5, color:T.dim }}>
          Powered by Claude Sonnet · Always review AI-generated content before using
        </div>
      </div>
      </section>
      </TierGate>

      </div></>
  );
}
